import { faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type KeyboardEvent, useRef, useState } from 'react';

import useStyles from './InlineEditText.styles';
import type { InlineEditTextProps } from './InlineEditText.types';

const InlineEditText = ({
  value,
  onSave,
  disabled = false,
  maxLength = 32,
  emptyDisplay = '--',
  placeholder = 'Enter value'
}: InlineEditTextProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [previousValue, setPreviousValue] = useState(value);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const isEditActive = useRef(false);
  const { classes } = useStyles({ disabled });

  if (previousValue !== value) {
    setPreviousValue(value);
    setDraft(null);
    setPendingValue(null);
  }

  const displayValue = pendingValue ?? value ?? '';
  const inputValue = draft ?? displayValue;

  const normalizeValue = (rawValue: string) => {
    const trimmedValue = rawValue.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  };

  const handleCancel = () => {
    isEditActive.current = false;
    setDraft(null);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!isEditActive.current || disabled) return;
    handleCancel();
    const nextValue = normalizeValue(inputValue);
    const currentValue = normalizeValue(displayValue);
    const nextDisplayValue = nextValue ?? '';

    if (nextValue !== currentValue) {
      setPendingValue(nextDisplayValue);
      onSave(nextValue);
    }
  };

  const handleStartEdit = () => {
    if (!disabled) {
      isEditActive.current = true;
      setIsEditing(true);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        handleSave();
        return;

      case 'Escape':
        event.preventDefault();
        handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Box className={classes.inputContainer}>
        <TextField
          autoFocus
          fullWidth
          onBlur={handleSave}
          onChange={(event) => !disabled && setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          size="small"
          value={inputValue}
          variant="outlined"
          slotProps={{
            htmlInput: {
              maxLength,
              readOnly: disabled
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box className={classes.displayContainer} onClick={handleStartEdit}>
      <Typography className={classes.valueText} variant="body1">
        {displayValue || emptyDisplay}
      </Typography>
      {!disabled && (
        <IconButton
          className={`${classes.editIcon} inline-edit-icon`}
          onClick={handleStartEdit}
          size="small"
          sx={{ p: 0.5, pr: 1 }}
        >
          <FontAwesomeIcon icon={faPen} size="xs" />
        </IconButton>
      )}
    </Box>
  );
};

InlineEditText.displayName = 'InlineEditText';

export default InlineEditText;
